import { useFormContext } from "react-hook-form";
import { useSelector } from "src/redux/hooks";
import { type ISection } from "src/interfaces";
import { convertFromUUIDKeys } from "src/redux/utils/convertFromUUIDKeys";
import { insertValues } from "src/redux/utils/insertValues";

const hasValue = (val: unknown): boolean => {
  if (val === null || val === undefined || val === "") return false;
  if (typeof val === "string") return val.trim().length > 0;
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return !Number.isNaN(val);
  if (typeof val === "bigint") return true;
  return false;
};

const isBlankRow = (row: Record<string, any>) => {
  if (!row) return true;

  // Check if any col has a meaningful value
  if (
    row.cols &&
    Object.values(row.cols).some((col: any) => hasValue(col?.value))
  ) {
    return false;
  }

  // Check if any item has a meaningful value
  if (
    row.items &&
    Object.values(row.items).some((item: any) => hasValue(item?.value))
  ) {
    return false;
  }

  // Check if any subrow has meaningful content
  if (
    row.subrows &&
    Array.isArray(row.subrows) &&
    row.subrows.some((subrow: any) => !isBlankRow(subrow))
  ) {
    return false;
  }

  return true;
};

const removeBlankRows = (sections: ISection[]) => {
  let removedBlankRows = false;

  const cleanSection = (section: Record<string, any>) => {
    const cleanedSection = { ...section };

    if (Array.isArray(cleanedSection.rows)) {
      // Only remove blank rows if there's more than one row
      if (cleanedSection.rows.length > 1) {
        const rowKeepMap = cleanedSection.rows.map((row: any) => {
          const keep = !isBlankRow(row as Record<string, any>);
          if (!keep) removedBlankRows = true;
          return keep;
        });

        cleanedSection.rows = cleanedSection.rows.filter(
          (_: any, index: number) => rowKeepMap[index],
        );

        if (Array.isArray(cleanedSection.savedRows)) {
          cleanedSection.savedRows = cleanedSection.savedRows.filter(
            (_: boolean, index: number) => rowKeepMap[index],
          );
        }
      }
    }

    if (Array.isArray(cleanedSection.subSections)) {
      cleanedSection.subSections = cleanedSection.subSections.map(cleanSection);
    }

    if (Array.isArray(cleanedSection.sections)) {
      cleanedSection.sections = cleanedSection.sections.map(cleanSection);
    }

    return cleanedSection;
  };

  const cleanedSections = sections.map(cleanSection);

  return { sections: cleanedSections, removedBlankRows };
};

export const convertLbEntryForSave = (
  lbentry: Record<string, any>,
  values: Record<string, any>,
  manualSave: boolean = false,
  removeBlankRowsOnContinuousEntry: boolean = false,
) => {
  const modifiedLogbook = insertValues(values, lbentry.sections, manualSave);
  const cleanedLogbook =
    removeBlankRowsOnContinuousEntry && lbentry.continousEntry
      ? removeBlankRows(modifiedLogbook)
      : { sections: modifiedLogbook, removedBlankRows: false };

  const originalLogbookVersion = convertFromUUIDKeys(
    cleanedLogbook.sections as ISection[],
  );
  const objToSend = {
    ...lbentry,
    sections: originalLogbookVersion,
  };

  return {
    payload: JSON.stringify(objToSend),
    removedBlankRows: cleanedLogbook.removedBlankRows,
  };
};

/**
 * Custom hook to convert logbook entry for saving.
 *
 * @param manualSave - An optional boolean indicating whether the save is manual.
 * @returns A function to convert logbook entry data into a JSON string for saving.
 */
export const useConvertLbEntry = (manualSave: boolean = false) => {
  const { lbentry } = useSelector(state => state.generatorSlice);
  const { getValues } = useFormContext();
  /**
   * Converts logbook entry data into a JSON string.
   *
   * @param data - An optional object containing the logbook entry data to convert.
   * @returns A JSON string representing the converted logbook entry.
   */
  const convertLbEntry = (data?: Record<string, any>) => {
    const values = data ?? getValues();
    return convertLbEntryForSave(lbentry, values, manualSave).payload;
  };

  return convertLbEntry;
};
